import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { XMarkIcon } from '@heroicons/react/24/outline';

export default function CustomDateRangeModal({ 
  isOpen, 
  onClose, 
  onApply, 
  currentRange = null,
  isPeopleContext = false // NEW
}) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedPreset, setSelectedPreset] = useState('');
  const [compareWithPreviousYear, setCompareWithPreviousYear] = useState(true);
  const [errors, setErrors] = useState({});

  // NEW: Salary period helper (20th → 20th)
  const getCurrentSalaryPeriod = () => {
    const today = moment().startOf('day');
    const thisMonth20 = today.clone().date(20).startOf('day');
    if (today.isSameOrAfter(thisMonth20)) {
      // 20th this month to 19th next month (inclusive)
      return {
        start: thisMonth20,
        end: thisMonth20.clone().add(1, 'month').date(19).endOf('day'),
      };
    } else {
      // 20th last month to 19th this month (inclusive)
      return {
        start: thisMonth20.clone().subtract(1, 'month'),
        end: thisMonth20.clone().subtract(1, 'day').endOf('day'),
      };
    }
  };

  // Generate presets dynamically
  const getPresets = () => {
    if (isPeopleContext) {
      // People: Only two options
      const salary = getCurrentSalaryPeriod();
      return [
        {
          id: 'this_month',
          label: 'This month',
          start: moment().startOf('month'),
          end: moment().endOf('month'),
        },
        {
          id: 'salary_20',
          label: 'This salary period (20th → 20th)',
          start: salary.start,
          end: salary.end,
        },
      ];
    }

    // Projects: keep all existing presets
    return [
      {
        id: 'ytd',
        label: 'Year-to-Date',
        start: moment().startOf('year'),
        end: moment()
      },
      {
        id: 'q1',
        label: 'Q1 (Jan-Mar)',
        start: moment().startOf('year'),
        end: moment().startOf('year').add(2, 'months').endOf('month')
      },
      {
        id: 'q2', 
        label: 'Q2 (Apr-Jun)',
        start: moment().startOf('year').add(3, 'months'),
        end: moment().startOf('year').add(5, 'months').endOf('month')
      },
      {
        id: 'q3',
        label: 'Q3 (Jul-Sep)', 
        start: moment().startOf('year').add(6, 'months'),
        end: moment().startOf('year').add(8, 'months').endOf('month')
      },
      {
        id: 'q4',
        label: 'Q4 (Oct-Dec)',
        start: moment().startOf('year').add(9, 'months'),
        end: moment().startOf('year').add(11, 'months').endOf('month')
      },
      {
        id: 'h1',
        label: 'H1 (Jan-Jun)',
        start: moment().startOf('year'),
        end: moment().startOf('year').add(5, 'months').endOf('month')
      },
      {
        id: 'h2',
        label: 'H2 (Jul-Dec)', 
        start: moment().startOf('year').add(6, 'months'),
        end: moment().endOf('year')
      },
      {
        id: 'last30',
        label: 'Last 30 days',
        start: moment().subtract(30, 'days'),
        end: moment()
      },
      {
        id: 'last90',
        label: 'Last 90 days',
        start: moment().subtract(90, 'days'), 
        end: moment()
      }
    ];
  };

  const presets = getPresets();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (currentRange) {
        setStartDate(moment(currentRange.start).format('YYYY-MM-DD'));
        setEndDate(moment(currentRange.end).format('YYYY-MM-DD'));
        setCompareWithPreviousYear(currentRange.compareWithPreviousYear || false);
      } else {
        // Default preset differs by context
        const defaultPreset = isPeopleContext ? 'this_month' : 'ytd';
        setSelectedPreset(defaultPreset);
        handlePresetSelect(defaultPreset);
      }
    } else {
      setStartDate('');
      setEndDate('');
      setSelectedPreset('');
      setErrors({});
    }
  }, [isOpen, currentRange, isPeopleContext]);

  const handlePresetSelect = (presetId) => {
    const preset = presets.find(p => p.id === presetId);
    if (preset) {
      setSelectedPreset(presetId);
      setStartDate(preset.start.format('YYYY-MM-DD'));
      setEndDate(preset.end.format('YYYY-MM-DD'));
      setErrors({});
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!startDate) newErrors.startDate = "Select start date";
    if (!endDate) newErrors.endDate = "Select end date";

    if (startDate && endDate) {
      const start = moment(startDate);
      const end = moment(endDate);
      
      if (end.isBefore(start)) {
        newErrors.endDate = "End date must be after start date";
      }
      
      // Check if range is too large (max 2 years)
      if (end.diff(start, 'years', true) > 2) {
        newErrors.endDate = "Period cannot exceed 2 years";
      }
      
      // Check if start date is too far back
      if (start.isBefore(moment().subtract(2, 'years'))) {
        newErrors.startDate = "Start date cannot be more than 2 years ago";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApply = () => {
    if (!validateForm()) return;

    const range = {
      start: startDate,
      end: endDate,
      compareWithPreviousYear,
      label: selectedPreset ? presets.find(p => p.id === selectedPreset)?.label : 'Custom Range'
    };

    onApply(range);
    onClose();
  };

  const getPreviewText = () => {
    if (!startDate || !endDate) return '';
    
    const start = moment(startDate);
    const end = moment(endDate);
    const days = end.diff(start, 'days') + 1;
    
    return `${days} days (${start.format('D MMM')} - ${end.format('D MMM YYYY')})`;
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        // Kun luk hvis man klikker på overlay'et og ikke på modal indholdet
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-[#0D1A27] text-white rounded-lg border border-[#273C61] w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#273C61]">
          <h2 className="text-lg font-semibold text-white">Select Period</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-[#273C61] rounded-full transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* Preset Options */}
          <div>
            <h3 className="text-sm font-bold text-white/80 mb-3">Quick Select</h3>

            {/* People: simple single-column with two buttons */}
            {isPeopleContext ? (
              <div className="space-y-2">
                {presets.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => handlePresetSelect(preset.id)}
                    className={`w-full p-2 text-sm rounded-md border transition-colors ${
                      selectedPreset === preset.id
                        ? 'bg-[#FF7800] border-[#FF7800] text-white font-medium'
                        : 'border-[#273C61] hover:bg-[#273C61] text-white'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            ) : (
              // Projects: keep existing two-column layout
              <div className="grid grid-cols-2 gap-4">
                {/* Left Column: Year-to-Date and Quarters */}
                <div className="space-y-2">
                  {presets.filter(p => ['ytd', 'q1', 'q2', 'q3', 'q4'].includes(p.id)).map((preset) => (
                    <button
                      key={preset.id}
                      onClick={() => handlePresetSelect(preset.id)}
                      className={`w-full p-2 text-sm rounded-md border transition-colors ${
                        selectedPreset === preset.id
                          ? 'bg-[#FF7800] border-[#FF7800] text-white font-medium'
                          : 'border-[#273C61] hover:bg-[#273C61] text-white'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                
                {/* Right Column: Half-years and Last X days */}
                <div className="space-y-2">
                  {presets.filter(p => ['h1', 'h2', 'last30', 'last90'].includes(p.id)).map((preset) => (
                    <button
                      key={preset.id}
                      onClick={() => handlePresetSelect(preset.id)}
                      className={`w-full p-2 text-sm rounded-md border transition-colors ${
                        selectedPreset === preset.id
                          ? 'bg-[#FF7800] border-[#FF7800] text-white font-medium'
                          : 'border-[#273C61] hover:bg-[#273C61] text-white'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Custom Date Range */}
          <div>
            <h3 className="text-sm font-bold text-white/80 mb-3">Or select specific dates</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1">From Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setSelectedPreset(''); // Clear preset when manually changing
                      setErrors(prev => ({ ...prev, startDate: '' }));
                    }}
                    className="w-full p-2 border border-[#273C61] rounded-md focus:ring-2 focus:ring-[#FF7800] focus:border-[#FF7800] bg-[#111e2e] text-white"
                  />
                  {errors.startDate && (
                    <div className="text-red-500 text-xs mt-1">{errors.startDate}</div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1">To Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setSelectedPreset(''); // Clear preset when manually changing
                      setErrors(prev => ({ ...prev, endDate: '' }));
                    }}
                    className="w-full p-2 border border-[#273C61] rounded-md focus:ring-2 focus:ring-[#FF7800] focus:border-[#FF7800] bg-[#111e2e] text-white"
                  />
                  {errors.endDate && (
                    <div className="text-red-500 text-xs mt-1">{errors.endDate}</div>
                  )}
                </div>
              </div>

              {/* Preview */}
              {getPreviewText() && (
                <div className="text-sm text-white/70 bg-[#111e2e] border border-[#273C61] rounded p-2">
                  📅 {getPreviewText()}
                </div>
              )}
            </div>
          </div>

          {/* Compare with Previous Year */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={compareWithPreviousYear}
                onChange={(e) => setCompareWithPreviousYear(e.target.checked)}
                className="mr-2 h-4 w-4 accent-[#FF7800] focus:ring-[#FF7800] border-[#273C61] rounded"
              />
              <span className="text-sm text-white/80">Compare with same period last year</span>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-[#273C61]">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white/70 bg-[#273C61] hover:bg-[#205799] rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-2 text-sm font-medium text-white bg-[#0D1A27] hover:bg-[#273C61] rounded-md transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}